import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  AutoComplete,
  AutoCompleteChangeEvent,
  AutoCompleteSelectEvent,
} from 'primereact/autocomplete';
import { Button } from 'primereact/button';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  endpointSearchPcCompaniesGET,
  SearchPcCompaniesResponse,
} from '~/api/def/endpoints/portfolioCompanyEndpoint';
import { useDocumentsUrlParams } from './useDocumentsUrlParams';
import { useDocumentsUrlParamsPortfolio } from './useDocumentsUrlParamsPortfolio';
import { useDocumentCache } from '~/atoms/document/useDocumentCache';
import {
  endpointFundDocumentPATCH,
  FundDocument,
  FundDocumentPatchPayloadArgument,
} from '~/api/def/endpoints/fundEndpoint';
import { toastAtom } from '~/atoms/toastAtom';
import { useAtom } from 'jotai';
import { Toast } from 'primereact/toast';

export const EditDocumentSchema = z.object({
  portfolioCompanyId: z.string(),
});

type ValidationSchema = z.infer<typeof EditDocumentSchema>;

interface EditDocumentFormProps {
  fundId: string;
  onHide: () => void;
  source?: string;
  onSubmitComplete?: () => void;
}

export const EditDocumentForm = ({
  fundId,
  onHide,
  source = 'fundDocuments',
  onSubmitComplete,
}: EditDocumentFormProps) => {
  const [toastCurrent] = useAtom<Toast | null>(toastAtom);
  const [selectedDocument, setSelectedDocument] = useState<FundDocument | null>(
    null,
  );
  const [value, setValue] = useState('');
  const [portfolioCompanies, setPortfolioCompanies] = useState<
    SearchPcCompaniesResponse[]
  >([]);

  const { activeDocumentId } =
    source === 'fundDocuments'
      ? useDocumentsUrlParams()
      : useDocumentsUrlParamsPortfolio();

  const { getDocument } = useDocumentCache();

  const { mutateAsync, isPending } = useMutation({
    mutationFn: (data: FundDocumentPatchPayloadArgument) => {
      if (selectedDocument?.fundId && selectedDocument?.id) {
        return endpointFundDocumentPATCH(
          selectedDocument.fundId,
          selectedDocument.id,
          data,
        );
      }
      throw new Error('Selected document is missing fundId or id');
    },
    onSuccess: () => {
      toastCurrent?.show({
        severity: 'success',
        detail: 'Document updated successfully.',
      });
      onSubmitComplete && onSubmitComplete();
    },
    onError: () => {
      toastCurrent?.show({
        severity: 'error',
        detail: 'Something went wrong while updating document.',
      });
    },
    onSettled: async () => {
      onHide();
    },
  });

  const { data, refetch } = useQuery({
    queryKey: [`searchPcCompaniesGet/@{fundId}`, fundId],
    queryFn: () => endpointSearchPcCompaniesGET(fundId, value),
    enabled: false,
  });

  const onSubmit = async (formData: ValidationSchema) => {
    if (
      formData.portfolioCompanyId ===
      (selectedDocument?.portfolioCompanyId ?? '')
    ) {
      return onHide();
    }
    await mutateAsync({
      portfolioCompanyId: formData.portfolioCompanyId || null,
    });
  };

  const {
    control,
    handleSubmit,
    setValue: setFormValue,
  } = useForm<ValidationSchema>({
    resolver: zodResolver(EditDocumentSchema),
    defaultValues: {
      portfolioCompanyId: selectedDocument?.portfolioCompanyId || '',
    },
  });

  useEffect(() => {
    setPortfolioCompanies(data ? [...data] : []);
  }, [data]);

  useEffect(() => {
    async function fetchData() {
      if (activeDocumentId) {
        const doc = await getDocument(activeDocumentId);
        if (doc) {
          setSelectedDocument(doc);
        }
      }
    }
    fetchData();
  }, [activeDocumentId, getDocument]);

  useEffect(() => {
    setValue(selectedDocument?.portfolioCompanyName || '');
    setFormValue(
      'portfolioCompanyId',
      selectedDocument?.portfolioCompanyId || '',
    );
  }, [selectedDocument, setFormValue]);

  return (
    <div className="flex justify-content-between flex-column h-full">
      <form className="p-3">
        <div className="grid align-content-start">
          <div className="col-12 flex flex-column gap-1">
            <label htmlFor="portfolioCompanyId">Portfolio Company</label>
            <Controller
              name="portfolioCompanyId"
              control={control}
              render={({ field }) => (
                <AutoComplete
                  {...field}
                  field="name"
                  id="portfolioCompanyId"
                  value={value}
                  onChange={(e: AutoCompleteChangeEvent) => {
                    setValue(e.value);
                  }}
                  placeholder="Search Portfolio Company"
                  suggestions={portfolioCompanies}
                  onSelect={(e: AutoCompleteSelectEvent) => {
                    setFormValue('portfolioCompanyId', e.value.id);
                  }}
                  onClear={() => setFormValue('portfolioCompanyId', '')}
                  dropdown
                  completeMethod={() => refetch()}
                  showEmptyMessage
                  forceSelection
                />
              )}
            />
          </div>
        </div>
      </form>
      <div
        className="flex justify-content-end p-3 gap-2"
        style={{
          borderTop: '1px solid #e9ecef',
        }}
      >
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={() => onHide()}
          className="p-button-text"
        />
        <Button
          type="submit"
          label="Save"
          icon={isPending ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
          disabled={isPending}
          onClick={handleSubmit(onSubmit)}
        />
      </div>
    </div>
  );
};
